import {
  createImageUpload,
  UploadImagesPlugin,
  type UploadFn,
} from '@/components/tiptap-editor/plugins'
import { cn } from '@/utils/cn'
import { Image } from '@blogging/ui'
import { mergeAttributes, Node } from '@tiptap/core'
import type { EditorView } from '@tiptap/pm/view'
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { cx } from 'class-variance-authority'
import React from 'react'

export interface ImageExtensionOptions {
  onUpload?: (file: File) => Promise<unknown>
  validateFn?: (file: File) => boolean
  imageClass?: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imageUpload: {
      uploadImage: (file: File) => ReturnType
    }
  }
}

// React Component for the Image Node View
// Simple display component - ImageModeToolbar handles mode switching
const ImageNodeComponent: React.FC<NodeViewProps> = ({ node, selected }) => {
  const { src, alt, title, width, height, mode } = node.attrs
  const imageMode = mode || 'normal'

  return (
    <NodeViewWrapper
      className={cn('node-image relative', {
        'ring-2 ring-primary': selected,
      })}
      data-image-node="true"
      data-width={imageMode}
    >
      {src ? (
        <Image
          src={src}
          alt={alt || ''}
          title={title || ''}
          width={width || undefined}
          height={height || undefined}
          mode={imageMode}
        />
      ) : (
        <div className="p-8 border-2 border-dashed border-border rounded-lg text-center text-muted-foreground">
          <div className="text-2xl mb-2">📷</div>
          <div>No image</div>
        </div>
      )}
    </NodeViewWrapper>
  )
}

// TipTap Extension Definition - Component-based Image
export const ImageExtension = Node.create<ImageExtensionOptions>({
  name: 'image',

  group: 'block',

  atom: true,

  addOptions() {
    return {
      onUpload: undefined,
      validateFn: undefined,
      imageClass: cx('opacity-40 rounded-lg border border-stone-200'),
    }
  },

  addStorage() {
    return {
      uploadFn: null as UploadFn | null,
    }
  },

  onCreate() {
    // Create the upload function from options when the extension is initialized
    if (this.options.onUpload) {
      this.storage.uploadFn = createImageUpload({
        onUpload: this.options.onUpload,
        validateFn: this.options.validateFn,
      })
    }
  },

  addAttributes() {
    return {
      src: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-src') || '',
        renderHTML: () => ({}), // Stored in data-src, not img src
      },
      alt: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-alt') || '',
        renderHTML: () => ({}), // Stored in data-alt
      },
      title: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-title') || '',
        renderHTML: () => ({}), // Stored in data-title
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const width = element.getAttribute('data-width')
          return width ? (isNaN(Number(width)) ? width : Number(width)) : null
        },
        renderHTML: () => ({}), // Stored in data-width
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height = element.getAttribute('data-height')
          return height
            ? isNaN(Number(height))
              ? height
              : Number(height)
            : null
        },
        renderHTML: () => ({}), // Stored in data-height
      },
      mode: {
        default: 'normal',
        parseHTML: (element) => element.getAttribute('data-mode') || 'normal',
        renderHTML: (attributes) => {
          return { 'data-mode': attributes.mode || 'normal' }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="image"]',
        getAttrs: (element) => {
          const domElement = element as HTMLElement
          // Read from data attributes (component-based approach)
          return {
            src: domElement.getAttribute('data-src') || '',
            alt: domElement.getAttribute('data-alt') || '',
            title: domElement.getAttribute('data-title') || '',
            width: (() => {
              const width = domElement.getAttribute('data-width')
              return width
                ? isNaN(Number(width))
                  ? width
                  : Number(width)
                : null
            })(),
            height: (() => {
              const height = domElement.getAttribute('data-height')
              return height
                ? isNaN(Number(height))
                  ? height
                  : Number(height)
                : null
            })(),
            mode: domElement.getAttribute('data-mode') || 'normal',
          }
        },
      },
      // Also support legacy img tags for backward compatibility
      {
        tag: 'img[data-image-node="true"]',
        getAttrs: (element) => {
          const domElement = element as HTMLElement
          return {
            src: domElement.getAttribute('src') || '',
            alt: domElement.getAttribute('alt') || '',
            title: domElement.getAttribute('title') || '',
            width: (() => {
              const width = domElement.getAttribute('width')
              return width
                ? isNaN(Number(width))
                  ? width
                  : Number(width)
                : null
            })(),
            height: (() => {
              const height = domElement.getAttribute('height')
              return height
                ? isNaN(Number(height))
                  ? height
                  : Number(height)
                : null
            })(),
            mode: domElement.getAttribute('data-mode') || 'normal',
          }
        },
      },
    ]
  },

  // Render the image node - store component data instead of HTML
  // The site app will render this as a React component
  renderHTML({ HTMLAttributes, node }) {
    const { src, alt, title, width, height, mode } = node.attrs

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'image',
        'data-component': 'image',
        'data-src': src || '',
        'data-alt': alt || '',
        'data-title': title || '',
        'data-width': width ? String(width) : '',
        'data-height': height ? String(height) : '',
        'data-mode': mode || 'normal',
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeComponent)
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
      uploadImage:
        (file: File) =>
        ({ view }: { view: EditorView }) => {
          const uploadFn = this.storage.uploadFn as UploadFn | null
          if (!uploadFn) {
            console.warn(
              'Image upload not configured. Please provide onUpload option.',
            )
            return false
          }

          const pos = view.state.selection.from
          uploadFn(file, view as EditorView, pos)
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const uploadFn = this.storage.uploadFn as UploadFn | null

    return [
      UploadImagesPlugin({
        imageClass:
          this.options.imageClass ||
          cx('opacity-40 rounded-lg border border-stone-200'),
        uploadFn,
      }),
    ]
  },
})
